import React, { useEffect, useState } from 'react';
import {
    Button,
    Chip,
    DialogContent,
    DialogTitle,
    Divider,
    Modal,
    ModalDialog,
    Stack,
    Typography,
} from '@mui/joy';
import Box from '@mui/joy/Box';
import WarningAmberRounded from '@mui/icons-material/WarningAmberRounded';
import PaymentRounded from '@mui/icons-material/PaymentRounded';
import AccountBalanceWalletRounded from '@mui/icons-material/AccountBalanceWalletRounded';
import { useLocation, useNavigate } from 'react-router-dom';
import GeneralApiService from '../../axios/Axios';
import { useUser } from '../dashboard-components/UserContext';
import { hasOverdueUnpaidFranchiseFee, isOverdueUnpaidFranchiseFee } from '../../lib/franchise-fee-lock';

interface FranchiseFee {
    id: number;
    period_start: string;
    period_end: string;
    amount: number;
    status: string;
    created_at?: string | null;
    franchise_location: string;
}

const DISMISS_KEY = 'franchise-alert-dismissed-at';
const COOLDOWN_MS = 12 * 60 * 60 * 1000;

function isDismissedRecently(): boolean {
    const dismissed = localStorage.getItem(DISMISS_KEY);
    if (!dismissed) return false;
    const elapsed = Date.now() - parseInt(dismissed, 10);
    return elapsed < COOLDOWN_MS;
}

export default function FranchiseAlertModal() {
    const [open, setOpen] = useState(false);
    const [pendingFees, setPendingFees] = useState<FranchiseFee[]>([]);
    const [leadBalance, setLeadBalance] = useState<number | null>(null);
    const [loaded, setLoaded] = useState(false);
    const [dismissedOverdueFeeAlert, setDismissedOverdueFeeAlert] = useState(false);

    const userContext = useUser() as { user: { location_permissions?: string[] } | null; isLoading: boolean } | null;
    const user = userContext?.user ?? null;
    const userLoading = userContext?.isLoading ?? true;
    const location = useLocation();
    const navigate = useNavigate();
    const selectedLocation = localStorage.getItem('location');

    const hasAllLocationAccess = user?.location_permissions?.includes('franchise.location.all');
    const hasNegativeBalance = leadBalance !== null && leadBalance <= -50;
    const hasPendingFees = pendingFees.length > 0;
    const hasOverduePendingFees = hasOverdueUnpaidFranchiseFee(pendingFees);
    const hasIssues = hasPendingFees || hasNegativeBalance;

    useEffect(() => {
        if (userLoading) return;
        if (hasAllLocationAccess) {
            setLoaded(true);
            return;
        }
        if (!selectedLocation || selectedLocation === 'All') {
            setLoaded(true);
            return;
        }

        let cancelled = false;

        const fetchAlerts = async () => {
            try {
                const [feesData, adsData] = await Promise.all([
                    GeneralApiService.getFranchiseFees(),
                    GeneralApiService.getToggledAds(),
                ]);

                if (cancelled) return;

                const pending = Array.isArray(feesData)
                    ? feesData.filter((f: FranchiseFee) => f.status !== 'paid')
                    : [];
                const balance = adsData?.lead_balance ?? null;

                setPendingFees(pending);
                setLeadBalance(balance);

                const shouldShow = pending.length > 0 || (balance !== null && balance <= -50);
                const hasOverduePending = hasOverdueUnpaidFranchiseFee(pending);
                if (shouldShow && (hasOverduePending ? !dismissedOverdueFeeAlert : !isDismissedRecently())) {
                    setOpen(true);
                }
            } catch (err) {
                console.error('Failed to fetch franchise alerts:', err);
            } finally {
                if (!cancelled) setLoaded(true);
            }
        };

        fetchAlerts();
        return () => { cancelled = true; };
    }, [selectedLocation, userLoading, hasAllLocationAccess, location.pathname, dismissedOverdueFeeAlert]);

    const handleDismiss = () => {
        if (hasOverduePendingFees) {
            setDismissedOverdueFeeAlert(true);
        }
        localStorage.setItem(DISMISS_KEY, Date.now().toString());
        setOpen(false);
    };

    if (!loaded || !hasIssues || !open) {
        return null;
    }
    const displayedFees = hasOverduePendingFees
        ? pendingFees.filter(isOverdueUnpaidFranchiseFee)
        : pendingFees;
    const totalOwed = displayedFees.reduce((sum, f) => sum + f.amount, 0);

    return (
        <Modal open={open} onClose={handleDismiss}>
            <ModalDialog
                variant="outlined"
                role="alertdialog"
                sx={{
                    maxHeight: '80vh',
                    overflowY: 'auto',
                    minWidth: { xs: 'auto', sm: 440 },
                    width: { xs: '95vw', sm: 'auto' },
                    maxWidth: { xs: '95vw', sm: '500px' },
                }}
            >
                <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <WarningAmberRounded color="warning" />
                    {hasOverduePendingFees ? 'CRM Access Limited' : 'Action Required'}
                </DialogTitle>
                <Divider />
                <DialogContent>
                    <Stack spacing={2.5} sx={{ mt: 1 }}>
                        {hasOverduePendingFees && (
                            <Box
                                sx={{
                                    p: 1.5,
                                    borderRadius: 'sm',
                                    bgcolor: 'danger.softBg',
                                    border: '1px solid',
                                    borderColor: 'danger.outlinedBorder',
                                }}
                            >
                                <Typography level="title-sm" color="danger" sx={{ mb: 0.5 }}>
                                    Your CRM access is limited until overdue franchise fees are paid.
                                </Typography>
                                <Typography level="body-sm">
                                    You can still access the Franchise Fees page to view and pay the outstanding invoices.
                                    Other CRM pages will stay locked until the overdue fees are settled.
                                </Typography>
                            </Box>
                        )}

                        {hasPendingFees && (
                            <Box>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5 }}>
                                    <PaymentRounded sx={{ color: 'danger.500', fontSize: '1.2rem' }} />
                                    <Typography level="title-md" color="danger">
                                        {hasOverduePendingFees ? 'Overdue Franchise Fees' : 'Pending Franchise Fees'}
                                    </Typography>
                                    <Chip size="sm" color="danger" variant="solid">
                                        {displayedFees.length}
                                    </Chip>
                                </Box>
                                <Stack spacing={1}>
                                    {displayedFees.map((fee) => (
                                        <Box
                                            key={fee.id}
                                            sx={{
                                                display: 'flex',
                                                justifyContent: 'space-between',
                                                alignItems: 'center',
                                                p: 1.5,
                                                borderRadius: 'sm',
                                                bgcolor: 'danger.softBg',
                                                border: '1px solid',
                                                borderColor: 'danger.outlinedBorder',
                                            }}
                                        >
                                            <Box>
                                                <Typography level="body-sm" fontWeight="lg">
                                                    {fee.period_start} &ndash; {fee.period_end}
                                                </Typography>
                                                <Chip size="sm" variant="soft" color="warning" sx={{ mt: 0.5 }}>
                                                    {fee.status}
                                                </Chip>
                                            </Box>
                                            <Typography level="title-md" fontWeight="lg">
                                                &pound;{fee.amount.toFixed(2)}
                                            </Typography>
                                        </Box>
                                    ))}
                                    {displayedFees.length > 1 && (
                                        <Box sx={{ display: 'flex', justifyContent: 'flex-end', pt: 0.5 }}>
                                            <Typography level="body-sm" fontWeight="lg">
                                                Total: &pound;{totalOwed.toFixed(2)}
                                            </Typography>
                                        </Box>
                                    )}
                                </Stack>
                            </Box>
                        )}

                        {!hasOverduePendingFees && hasPendingFees && hasNegativeBalance && <Divider />}

                        {!hasOverduePendingFees && hasNegativeBalance && (
                            <Box>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                                    <AccountBalanceWalletRounded sx={{ color: 'danger.500', fontSize: '1.2rem' }} />
                                    <Typography level="title-md" color="danger">
                                        Negative Marketing Balance
                                    </Typography>
                                </Box>
                                <Box
                                    sx={{
                                        p: 1.5,
                                        borderRadius: 'sm',
                                        bgcolor: 'danger.softBg',
                                        border: '1px solid',
                                        borderColor: 'danger.outlinedBorder',
                                        textAlign: 'center',
                                    }}
                                >
                                    <Typography level="body-sm" sx={{ mb: 0.5 }}>
                                        Current Balance
                                    </Typography>
                                    <Typography level="h3" color="danger" fontWeight="lg">
                                        &pound;{leadBalance!.toFixed(2)}
                                    </Typography>
                                    <Typography level="body-xs" sx={{ mt: 0.5 }}>
                                        Please top up your marketing balance above negative.
                                    </Typography>
                                </Box>
                            </Box>
                        )}

                        <Typography level="body-xs" textColor="text.tertiary" sx={{ textAlign: 'center' }}>
                            {hasOverduePendingFees
                                ? 'This alert will reappear on every refresh until overdue franchise fees are paid.'
                                : 'This alert will reappear after 12 hours if these issues are not resolved.'}
                        </Typography>

                        {hasPendingFees && (
                            <Button
                                variant="solid"
                                color="danger"
                                onClick={() => { handleDismiss(); navigate('/dashboard/franchise-fees'); }}
                                fullWidth
                            >
                                Go to Franchise Fees
                            </Button>
                        )}
                        {!hasOverduePendingFees && hasNegativeBalance && (
                            <Button
                                variant="solid"
                                color="danger"
                                onClick={() => {
                                    handleDismiss();
                                    navigate('/dashboard/lead-generation');
                                }}
                                fullWidth
                            >
                                Go to Marketing Balance
                            </Button>
                        )}
                        <Button
                            variant="plain"
                            color="neutral"
                            onClick={handleDismiss}
                            fullWidth
                        >
                            I Acknowledge
                        </Button>
                    </Stack>
                </DialogContent>
            </ModalDialog>
        </Modal>
    );
}
